// Ubicación en el repo: src/lib/clinicSettings.js
// Apariencia de la clínica (modo oscuro + colores) — tabla `clinic_settings`
// (fila única, `id boolean primary key default true`), ya creada desde la
// migración inicial (0001) con su RLS (0003: lectura abierta a cualquier
// autenticado, escritura solo Admin). Mismo criterio que
// DEFAULT_SETTINGS/applySettings() del prototipo (línea ~347-442): los
// colores se aplican como CSS custom properties en <html>, y el modo
// oscuro usa una paleta fija propia (el color de fondo elegido no aplica
// ahí).
import { supabase } from './supabaseClient';

export const DEFAULT_SETTINGS = {
  bgColor: '#F4F6F7',
  primaryColor: '#0F5257',
  secondaryColor: '#2D8B90',
  sidebarColor: '#0A3A3E',
  darkMode: false,
  razonSocial: '',
  rut: '',
  direccion: '',
  correoContactoDatos: '',
  cancelLinkEnabled: false,
};

function rowToSettings(row) {
  return {
    bgColor: row.bg_color,
    primaryColor: row.primary_color,
    secondaryColor: row.secondary_color,
    sidebarColor: row.sidebar_color,
    darkMode: row.dark_mode,
    razonSocial: row.razon_social || '',
    rut: row.rut || '',
    direccion: row.direccion || '',
    correoContactoDatos: row.correo_contacto_datos || '',
    cancelLinkEnabled: !!row.cancel_link_enabled,
  };
}

export async function getClinicSettings() {
  const { data, error } = await supabase.from('clinic_settings').select('*').eq('id', true).single();
  if (error) throw error;
  return { ...DEFAULT_SETTINGS, ...rowToSettings(data) };
}

export async function updateClinicSettings(settings, actorId) {
  const { error } = await supabase
    .from('clinic_settings')
    .update({
      bg_color: settings.bgColor,
      primary_color: settings.primaryColor,
      secondary_color: settings.secondaryColor,
      sidebar_color: settings.sidebarColor,
      dark_mode: settings.darkMode,
      updated_by: actorId,
    })
    .eq('id', true);
  if (error) throw error;
}

// Separado de updateClinicSettings() a propósito: los campos legales no
// tienen vista previa inmediata (no son visuales) y se guardan desde una
// sub-pestaña distinta (LegalSettings.jsx) — mezclar los dos updates en uno
// solo forzaría a mandar siempre los colores aunque no hayan cambiado.
export async function updateClinicLegalInfo(legal, actorId) {
  const { error } = await supabase
    .from('clinic_settings')
    .update({
      razon_social: legal.razonSocial || null,
      rut: legal.rut || null,
      direccion: legal.direccion || null,
      correo_contacto_datos: legal.correoContactoDatos || null,
      updated_by: actorId,
    })
    .eq('id', true);
  if (error) throw error;
}

// Interruptor de la cancelación online por correo (29-sep-2026,
// DECISIONS.md #124, pedido de Nicolás): separado de updateClinicSettings()/
// updateClinicLegalInfo() por el mismo motivo que esas dos ya están
// separadas entre sí — un guardado propio, sin mezclar con colores ni
// datos legales que no cambiaron.
export async function updateCancelLinkEnabled(enabled, actorId) {
  const { error } = await supabase
    .from('clinic_settings')
    .update({ cancel_link_enabled: enabled, updated_by: actorId })
    .eq('id', true);
  if (error) throw error;
}

// Mismo criterio que applySettings() del prototipo: --primary/--accent/
// --primary-dark siguen la elección del usuario en ambos modos; en modo
// oscuro, --bg/--surface/--surface-alt/--border/--text/--text-muted usan
// una paleta fija (no la elegida en "Color de fondo").
export function applyClinicSettings(settings) {
  const root = document.documentElement.style;
  const primary = settings.primaryColor || DEFAULT_SETTINGS.primaryColor;
  const accent = settings.secondaryColor || DEFAULT_SETTINGS.secondaryColor;
  const sidebar = settings.sidebarColor || DEFAULT_SETTINGS.sidebarColor;
  root.setProperty('--primary', primary);
  root.setProperty('--accent', accent);
  root.setProperty('--primary-dark', sidebar);
  if (settings.darkMode) {
    root.setProperty('--bg', '#14181A');
    root.setProperty('--surface', '#1E2426');
    root.setProperty('--surface-alt', '#262E30');
    root.setProperty('--border', '#374144');
    root.setProperty('--text', '#E8EDEE');
    root.setProperty('--text-muted', '#9AA8AB');
  } else {
    root.setProperty('--bg', settings.bgColor || DEFAULT_SETTINGS.bgColor);
    root.setProperty('--surface', '#FFFFFF');
    root.setProperty('--surface-alt', '#EBF1F1');
    root.setProperty('--border', '#DCE3E5');
    root.setProperty('--text', '#1C2B2E');
    root.setProperty('--text-muted', '#5B6B6E');
  }
}
